import { batch, signal } from '@preact/signals-react';
import { Profile } from '../../../common/models/Profile';
import { ReportDone } from '../../../reports/common/models/Report';
import { Project } from '../../../reports/common/models/valueObjects/Project';

export enum ExportFormat {
  PRINT = 'print',
  CSV = 'csv',
  JIRA = 'jira',
  SERVICE_NOW = 'service_now',
}

export enum CheckedStatus {
  NOT_SELECTED = -1,
  PARTIALLY_SELECTED = 0,
  SELECTED = 1,
}

export class ReportExportState {
  private static instance: ReportExportState | null = null;

  readonly project = signal<Project | null>(null);
  readonly profile = signal<Profile | null>(null);
  readonly report = signal<ReportDone | null>(null);
  readonly selectedInstances = signal<{ instanceIds: Set<number>; rule: number }[]>([]);

  readonly includeFilteredRules = signal(false);

  readonly format = signal(ExportFormat.PRINT);

  static getInstance() {
    if (!ReportExportState.instance) {
      ReportExportState.instance = new ReportExportState();
    }
    return ReportExportState.instance;
  }

  setState(report: ReportDone) {
    batch(() => {
      this.project.value = report.getProject();
      this.profile.value = report.getProfile();
      this.report.value = report;
      this.selectedInstances.value = [];
      this.includeFilteredRules.value = false;
      this.format.value = ExportFormat.PRINT;
    });
  }

  reset() {
    batch(() => {
      this.project.value = null;
      this.profile.value = null;
      this.report.value = null;
      this.selectedInstances.value = [];
      this.includeFilteredRules.value = false;
      this.format.value = ExportFormat.PRINT;
    });
  }

  peek() {
    return {
      report: this.report.peek(),
      includeFilteredRules: this.includeFilteredRules.peek(),
      format: this.format.peek(),
    };
  }

  setIncludeFilteredRules(value: boolean) {
    this.includeFilteredRules.value = value;
  }

  setFormat(format: ExportFormat) {
    this.format.value = format;
  }

  getFormat() {
    return this.format.value;
  }

  getInstanceCount() {
    return this.selectedInstances.value.map((instance) => [...instance.instanceIds]).flat().length;
  }

  getIssueCount() {
    return this.selectedInstances.value.length;
  }

  selectInstance(instanceId: number, rule: number) {
    const index = this.selectedInstances.value.findIndex((x) => x.rule === rule);
    if (index > -1) {
      const newInstanceIdSet = this.selectedInstances.value[index].instanceIds.add(instanceId);
      this.selectedInstances.value.splice(index, 1);
      this.selectedInstances.value = [...this.selectedInstances.value, { instanceIds: newInstanceIdSet, rule }];
    } else {
      this.selectedInstances.value = [...this.selectedInstances.value, { instanceIds: new Set([instanceId]), rule }];
    }
    this.setFormat(ExportFormat.JIRA);
  }

  unselectInstance(instanceId: number, rule: number) {
    const index = this.selectedInstances.value.findIndex((x) => x.rule === rule);
    if (index > -1) {
      this.selectedInstances.value[index].instanceIds.delete(instanceId);
      const newInstanceIdSet = this.selectedInstances.value[index].instanceIds;
      this.selectedInstances.value.splice(index, 1);
      this.selectedInstances.value = [...this.selectedInstances.value, { instanceIds: newInstanceIdSet, rule }];

      // If removing the last ID for a rule, remove the item for that rule from the selectedInstances array
      if (this.selectedInstances.value[index].instanceIds.size === 0) {
        this.selectedInstances.value = this.selectedInstances.value.filter((x) => x.rule !== rule);
      }
    }
    if (this.selectedInstances.value.length === 0) this.setFormat(ExportFormat.PRINT);
  }

  selectAllInstances(instanceIds: number[], rule: number) {
    const index = this.selectedInstances.value.findIndex((x) => x.rule === rule);
    if (index > -1) {
      const newInstanceIdSet = new Set([...this.selectedInstances.value[index].instanceIds, ...instanceIds]);
      this.selectedInstances.value.splice(index, 1);
      this.selectedInstances.value = [...this.selectedInstances.value, { instanceIds: newInstanceIdSet, rule }];
    } else {
      this.selectedInstances.value = [...this.selectedInstances.value, { instanceIds: new Set(instanceIds), rule }];
    }
    this.setFormat(ExportFormat.JIRA);
  }

  unselectAllInstances(instanceIds: number[], rule: number) {
    const index = this.selectedInstances.value.findIndex((x) => x.rule === rule);
    if (index > -1) {
      const oldInstances = this.selectedInstances.value[index].instanceIds;
      instanceIds.forEach((id) => oldInstances.delete(id));

      // If removing the last ID for a rule, remove the obj for that rule from the selectedInstances array
      if (oldInstances.size === 0) {
        this.selectedInstances.value = this.selectedInstances.value.filter((x) => x.rule !== rule);
      } else {
        this.selectedInstances.value.splice(index, 1);
        this.selectedInstances.value = [...this.selectedInstances.value, { instanceIds: oldInstances, rule }];
      }
    }
    if (this.selectedInstances.value.length === 0) this.setFormat(ExportFormat.PRINT);
  }

  getSelectedInstanceIds() {
    return this.selectedInstances.value.map((instance) => [...instance.instanceIds]).flat();
  }

  getInstanceSelectionState(instanceId: number, rule: number) {
    const item = this.selectedInstances.value.find((x) => x.rule === rule);
    if (!item) {
      return CheckedStatus.NOT_SELECTED;
    }
    if (item.instanceIds.has(instanceId)) {
      return CheckedStatus.SELECTED;
    }
    return CheckedStatus.NOT_SELECTED;
  }

  getIssueSelectionState(availableIds: number[], rule: number) {
    const item = this.selectedInstances.value.find((x) => x.rule === rule);
    if (!item) {
      return CheckedStatus.NOT_SELECTED;
    }
    if (availableIds.every((id) => item.instanceIds.has(id))) {
      return CheckedStatus.SELECTED;
    }
    if (availableIds.some((id) => item.instanceIds.has(id))) {
      return CheckedStatus.PARTIALLY_SELECTED;
    }
    return CheckedStatus.NOT_SELECTED;
  }

  getIssueGroupSelectionState(availableIds: number[]) {
    const selectedInstanceIds = this.selectedInstances.value.map((instance) => [...instance.instanceIds]).flat();
    if (selectedInstanceIds.length === 0) {
      return CheckedStatus.NOT_SELECTED;
    }
    if (selectedInstanceIds.length === availableIds.length) {
      return CheckedStatus.SELECTED;
    }
    if (selectedInstanceIds.length > 0) {
      return CheckedStatus.PARTIALLY_SELECTED;
    }
    return CheckedStatus.NOT_SELECTED;
  }

  checkAvailableInstances(availableIds: number[]) {
    const availableIdsSet = new Set(availableIds);
    // create a set of all selected instances by mapping the selectedInstances object array into
    // an array of sets of the instances ids and reducing to a number array
    const selectedIdsArr = this.selectedInstances.value.map((obj) => obj.instanceIds).reduce((a, c) => a.concat([...c]), [] as number[]);
    const selectedIdsSet = new Set(selectedIdsArr);
    const diff = selectedIdsSet.difference(availableIdsSet);
    // If changing the filter toggle adds more ids than are selected the diff size will be 0
    // If some ids that are selected aren't available anymore the size will be greater than 0
    if (diff.size > 0) {
      diff.forEach((diffId) => {
        // Find the rule number for the instance id and unselect
        const index = this.selectedInstances.value.findIndex((selected) => selected.instanceIds.has(diffId));
        const ruleNumber = this.selectedInstances.value[index].rule;
        this.unselectInstance(diffId, ruleNumber);
      });
    }
  }
}
